import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeavePrompt } from '@hooks/useLeavePrompt';
import LeaveConfirmModal from '@components/LeaveConfirmModal';
import { getRailwayUrl } from '@lib/railwayUrl';
import { routes } from '@lib/routes';
import exifr from 'exifr';
import { createSHA256 } from 'hash-wasm';

const hasMeaningfulExifData = (exif: Record<string, any> | null): boolean => {
  if (!exif || Object.keys(exif).length === 0) return false;
  const importantFields = ['Make', 'Model', 'GPSLatitude', 'GPSLongitude', 'DateTimeOriginal', 'DateTime',
    'ImageWidth', 'ImageHeight', 'Software', 'ISO', 'FNumber', 'ExposureTime', 'FocalLength'];
  const hasImportant = importantFields.some(field => {
    const value = exif[field];
    return value !== null && value !== undefined && value !== '' &&
           !(Array.isArray(value) && value.length === 0) &&
           !(typeof value === 'object' && Object.keys(value).length === 0);
  });
  if (hasImportant) return true;
  return Object.entries(exif).some(([key, value]) => {
    if (importantFields.includes(key)) return false;
    return value !== null && value !== undefined && value !== '' &&
           !(Array.isArray(value) && value.length === 0) &&
           !(typeof value === 'object' && Object.keys(value).length === 0);
  });
};

interface FileData {
  file: File;
  preview: string | null;
  exifData?: Record<string, any>;
}

const MAX_IMAGE_SIZE = 50 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic'];
const FILE_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function Upload() {
  const navigate = useNavigate();
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [isProceeding, setIsProceeding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState<'hashing' | 'uploading' | 'starting-analysis' | null>(null);
  const { isBlocked, proceed, reset } = useLeavePrompt(!!fileData && !isProceeding);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const railwayUrl = getRailwayUrl();

  const startAnalysis = useCallback(async (
    nextFile: File,
    preview: string | null,
    exifData?: Record<string, any>,
  ) => {
    setIsUploading(true);
    setError(null);
    setIsProceeding(true);

    try {
      setUploadStage('hashing');
      const hash = await computeHash(nextFile);

      setUploadStage('uploading');
      const formData = new FormData();
      formData.set('file', nextFile);
      formData.set('fileHash', hash);
      formData.set('fileType', nextFile.type);
      formData.set('fileSize', String(nextFile.size));
      formData.set('selectedType', 'image');

      const uploadResponse = await fetch(`${railwayUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const text = await uploadResponse.text();
        throw new Error(`Upload failed: ${text.slice(0, 200)}`);
      }

      const { verificationId } = await uploadResponse.json() as { verificationId: string };

      localStorage.setItem('checkio_hash', hash);
      localStorage.setItem('checkio_verification_id', verificationId);
      localStorage.setItem('checkio_file', JSON.stringify({
        name: nextFile.name,
        size: nextFile.size,
        type: nextFile.type,
        selectedType: 'image',
        preview: preview || null,
      }));
      if (exifData && hasMeaningfulExifData(exifData)) {
        localStorage.setItem('checkio_exif', JSON.stringify(exifData));
      } else {
        localStorage.removeItem('checkio_exif');
      }

      setUploadStage('starting-analysis');
      const analyzeResponse = await fetch(`${railwayUrl}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verificationId }),
      });

      if (!analyzeResponse.ok) {
        const text = await analyzeResponse.text();
        throw new Error(`Failed to start analysis: ${text.slice(0, 200)}`);
      }

      navigate(routes.results(verificationId));
    } catch (err) {
      setIsProceeding(false);
      setIsUploading(false);
      setUploadStage(null);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  }, [navigate, railwayUrl]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_IMAGE_SIZE) {
      return `File too large. Maximum size: ${formatFileSize(MAX_IMAGE_SIZE)}`;
    }

    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      return `Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`;
    }

    return null;
  };

  const createPreview = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const extractExifData = async (file: File): Promise<Record<string, any> | null> => {
    try {
      if (!exifr?.parse) return null;
      const exifData = await exifr.parse(file, {
        pick: ['Make', 'Model', 'DateTimeOriginal', 'DateTime', 'Software',
          'ImageWidth', 'ImageHeight', 'Orientation', 'XResolution', 'YResolution',
          'ISO', 'FNumber', 'ExposureTime', 'FocalLength', 'Flash',
          'GPSLatitude', 'GPSLongitude', 'GPSAltitude'],
        translateKeys: false,
        translateValues: false,
      });
      if (!exifData || Object.keys(exifData).length === 0) {
        return await exifr.parse(file) || null;
      }
      return exifData;
    } catch {
      return null;
    }
  };

  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) { setError(validationError); return; }

    const preview = await createPreview(file);
    const exifData = await extractExifData(file);

    setFileData({ file, preview, exifData: exifData || undefined });

    localStorage.setItem('checkio_file', JSON.stringify({
      name: file.name, size: file.size, type: file.type, selectedType: 'image', preview: preview || null,
    }));
    if (exifData && hasMeaningfulExifData(exifData)) {
      localStorage.setItem('checkio_exif', JSON.stringify(exifData));
    } else {
      localStorage.removeItem('checkio_exif');
    }
    localStorage.removeItem('checkio_hash');
    localStorage.removeItem('checkio_verification_id');

    void startAnalysis(file, preview, exifData || undefined);
  }, [startAnalysis]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
  }, [handleFileSelect]);

  const handleChangeFile = () => {
    setFileData(null);
    setError(null);
    localStorage.removeItem('checkio_file');
    localStorage.removeItem('checkio_exif');
    localStorage.removeItem('checkio_hash');
    localStorage.removeItem('checkio_verification_id');
  };

  const computeHash = async (file: File): Promise<string> => {
    const sha256 = await createSHA256();
    sha256.init();
    const chunkSize = 2 * 1024 * 1024;
    let offset = 0;
    while (offset < file.size) {
      const chunk = file.slice(offset, offset + chunkSize);
      const buffer = await chunk.arrayBuffer();
      sha256.update(new Uint8Array(buffer));
      offset += chunkSize;
    }
    return sha256.digest('hex');
  };

  return (
    <>
      <LeaveConfirmModal open={isBlocked} onStay={reset} onLeave={proceed} />
      <div className="min-h-screen bg-veritas-gray-50 font-sans text-veritas-navy flex flex-col">
        <div className="app-header h-12 bg-veritas-navy flex items-center justify-between px-4 sm:px-6">
          <a href={routes.landing} className="flex items-center justify-center shrink-0" aria-label="Home">
            <img src="/brand/veritas-inverse.svg" alt="Veritas" className="h-8 w-auto" />
          </a>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          <h1 className="font-sans text-4xl md:text-5xl font-light text-veritas-navy text-center mb-4">
            {fileData ? 'Preparing analysis' : 'Upload an image'}
          </h1>
          <p className="font-sans text-base text-veritas-gray-500 text-center mb-12 max-w-xl">
            Image files only for the live demo. Audio and video analysis coming soon.
          </p>

          {!fileData && (
            <div className="w-full max-w-3xl">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`rounded-2xl border-2 border-dashed bg-white transition-all duration-200 ${
                  isDragging ? 'border-veritas-navy bg-veritas-gray-200' : 'border-veritas-navy/15'
                }`}
              >
                <div className="py-20 flex flex-col items-center gap-5 px-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-veritas-gray-200 flex items-center justify-center">
                    <svg className="w-8 h-8 text-veritas-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans font-semibold text-veritas-navy mb-1">Drop your image here</p>
                    <p className="font-sans text-sm text-veritas-gray-500">or click to browse your files</p>
                  </div>
                  <label className="cursor-pointer inline-flex items-center px-5 py-2.5 bg-veritas-navy text-white font-sans text-sm font-medium rounded-xl hover:bg-veritas-navy/90 transition-colors">
                    <input
                      type="file"
                      className="hidden"
                      accept={FILE_ACCEPT}
                      onChange={handleFileInput}
                    />
                    Choose image
                  </label>
                  <div className="space-y-1 text-xs text-veritas-gray-400">
                    <p>Image files only (JPEG, PNG, WebP, HEIC)</p>
                    <p title="Audio and video analysis coming soon.">
                      Audio and video analysis coming soon.
                    </p>
                    <p>Up to {formatFileSize(MAX_IMAGE_SIZE)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {fileData && (
            <div className="w-full max-w-3xl">
              <div className="bg-white rounded-2xl border border-veritas-navy/8 shadow-veritas-soft p-6 flex items-center gap-5">
                {fileData.preview ? (
                  <img src={fileData.preview} alt="Preview" className="w-20 h-20 object-cover rounded-xl shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-veritas-gray-200 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-semibold text-veritas-navy truncate mb-1">{fileData.file.name}</p>
                  <p className="font-sans text-sm text-veritas-gray-500">{formatFileSize(fileData.file.size)} &middot; {fileData.file.type}</p>
                  <p className="mt-2 text-xs text-veritas-gray-400">Image files only (JPEG, PNG, WebP, HEIC)</p>
                </div>
                <button onClick={handleChangeFile} className="shrink-0 text-sm text-veritas-gray-500 hover:text-veritas-navy font-sans transition-colors underline underline-offset-2">
                  Change
                </button>
              </div>

              <div className="mt-6 text-center">
                {isUploading ? (
                  <div className="inline-flex items-center gap-2 px-8 py-3.5 bg-veritas-navy text-white font-sans font-semibold rounded-xl">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {uploadStage === 'hashing'
                      ? 'Computing hash…'
                      : uploadStage === 'uploading'
                        ? 'Uploading…'
                        : 'Starting analysis…'}
                  </div>
                ) : (
                  <button
                    onClick={() => void startAnalysis(fileData.file, fileData.preview, fileData.exifData)}
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-veritas-navy text-white font-sans font-semibold rounded-xl hover:bg-veritas-navy/90 transition-colors"
                  >
                    Retry analysis
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12a7.5 7.5 0 111.94 5.03M4.5 12V6.75M4.5 12h5.25" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="w-full max-w-3xl mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 font-sans text-sm">{error}</div>
          )}

          <div className="mt-10 text-center space-y-2">
            <p className="font-sans text-sm text-veritas-gray-400">Audio and video analysis coming soon.</p>
            <p className="font-sans text-sm text-veritas-gray-400 flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Your file is hashed in-browser before analysis starts
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
